import { type SubjectCode } from '../learning/subjects'

/**
 * School Material Learning Journey (Slice 2, D-14.3): the shapes of a sample
 * material and of the learner's work on it, kept apart so every step stays
 * traceable:
 *
 *   Source → Extracted Content → Topic/Objective → Explanation/Practice →
 *   Attempt → Evidence → Next Action
 *
 * There is no extraction, AI or evidence backend behind these types. The only
 * materials are the DEMO set in `demo-materials.ts`, loaded in
 * local/development builds only (D-02). Each subject keeps its own answer
 * kinds and grading rules (`grade.ts`); nothing here is a shared academic model.
 */

/** A line of the original source, as printed on the (sample) page. */
export interface SourceLine {
  readonly id: string
  readonly text: string
  /** Part of `text` that is smudged on the page: drawn blurred, read uncertainly. */
  readonly unclear?: string
}

/** A piece of content "read" from the source. Simulated for DEMO samples. */
export interface ExtractedBlock {
  readonly id: string
  readonly label: string
  readonly text: string
  /** Reading is uncertain (e.g. blurred digit): shown as such, never as fact. */
  readonly uncertainNote: string | null
}

export interface WorkedExample {
  readonly problem: string
  readonly steps: readonly string[]
  readonly answer: string
}

/** Pre-authored explanation of the topic (DEMO; not generated by AI). */
export interface TopicExplanation {
  readonly keyIdea: string
  readonly points: readonly string[]
  readonly example: WorkedExample
  readonly commonMistake: string
  /** A simpler second explanation for "Con cần giải thích lại". */
  readonly explainAgain: string
}

/** A step of a guided solution: the learner tries it, then compares. */
export interface GuidedStep {
  readonly prompt: string
  readonly reveal: string
}

/** Pre-authored content for the four assistance modes (D-14.5). */
export interface QuestionHelp {
  readonly explain: string
  readonly hint: string
  readonly guided: readonly GuidedStep[]
  readonly full: readonly string[]
}

/** A known wrong answer and the misconception it points to. */
export interface KnownMistake {
  readonly answer: string
  readonly feedback: string
  /** Short, parent-readable name of the error pattern. */
  readonly tag: string
}

interface QuestionBase {
  readonly id: string
  readonly prompt: string
  /**
   * The extracted block this question comes from (traceability). For an
   * `extension` question, the block whose idea it practises.
   */
  readonly sourceBlockId: string
  /**
   * `extension`: a new practice question on the same idea, not taken from the
   * source; labelled as such. Omitted: taken from or directly about the source.
   */
  readonly origin?: 'extension'
  /** Why the correct answer is correct; shown after submission. */
  readonly explanation: string
  readonly help: QuestionHelp
  readonly mistakes?: readonly KnownMistake[]
  /** English text in the prompt/options, for `lang`. */
  readonly lang?: 'en'
}

export interface ChoiceOption {
  readonly id: string
  readonly text: string
}

/** Single choice: concepts, vocabulary, interpretation. */
export interface ChoiceQuestion extends QuestionBase {
  readonly kind: 'choice'
  readonly options: readonly ChoiceOption[]
  readonly correctOptionId: string
}

/** English short answer: accepted variants, case-insensitive. */
export interface TextQuestion extends QuestionBase {
  readonly kind: 'text'
  readonly accepted: readonly string[]
}

/** Mathematics fraction answer: any equivalent fraction is correct. */
export interface FractionQuestion extends QuestionBase {
  readonly kind: 'fraction'
  readonly numerator: number
  readonly denominator: number
}

/** Numeric answer with a tolerance and a fixed unit (Physics, Chemistry counts). */
export interface NumberQuestion extends QuestionBase {
  readonly kind: 'number'
  readonly value: number
  readonly tolerance: number
  readonly unit: string | null
}

/** Chemistry formula: case-sensitive symbols, subscripts as digits. */
export interface FormulaQuestion extends QuestionBase {
  readonly kind: 'formula'
  readonly accepted: readonly string[]
}

export type Question =
  ChoiceQuestion | TextQuestion | FractionQuestion | NumberQuestion | FormulaQuestion

export interface Topic {
  readonly id: string
  readonly title: string
  readonly objective: string
  /** "After this you can…", short child-facing outcomes. */
  readonly canDo: readonly string[]
  /** Extracted blocks the topic is based on (traceability). */
  readonly blockIds: readonly string[]
  readonly explanation: TopicExplanation
  readonly questions: readonly Question[]
}

export interface SampleMaterial {
  readonly id: string
  readonly subject: SubjectCode
  readonly title: string
  /** Page or section of the (sample) document, e.g. "Trang 2 · Bài 3". */
  readonly sourceRef: string
  readonly source: readonly SourceLine[]
  readonly extracted: readonly ExtractedBlock[]
  readonly topics: readonly Topic[]
  /** Always true: there are no real materials in this slice. */
  readonly isDemo: true
}

export type LoadSampleMaterials = () => Promise<readonly SampleMaterial[]>

export type HelpMode = 'explain' | 'hint' | 'guided' | 'full'

export const HELP_LABELS: Readonly<Record<HelpMode, string>> = {
  explain: 'Giải thích',
  hint: 'Gợi ý',
  guided: 'Giải từng bước',
  full: 'Lời giải đầy đủ',
}

/** One checked answer. Assistance is recorded with it (FR-38, D-14.5). */
export interface Attempt {
  readonly questionId: string
  /** The raw answer, e.g. an option ID, "5/6", "12" or "CH4". */
  readonly answer: string
  /** The answer as the learner would read it. */
  readonly display: string
  readonly correct: boolean
  readonly correctDisplay: string
  readonly mistake: KnownMistake | null
  /** Help opened before the answer was checked. */
  readonly helpBeforeSubmit: readonly HelpMode[]
}

/** Learner's own "do I understand?" answer: self-report, never mastery. */
export type SelfReport = 'understood' | 'partly' | 'again'

/**
 * What a finished DEMO run leaves for the parent preview. Deliberately has no
 * learner or family identifier: it is never linked to a real profile and lives
 * only in memory for the current page.
 */
export interface DemoRunReport {
  readonly sample: SampleMaterial
  readonly topic: Topic
  readonly attempts: readonly Attempt[]
  readonly selfReport: SelfReport | null
}
